import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import useBackToClose from '../../hooks/useBackToClose';
import haptic from '../../utils/haptic';
import { useAuth } from '../../context/AuthContext';
import { personMatchesName } from '../../utils/personnel';
import { checklistBadge, operationBaseEventName, operationName } from './agendaUtils';
import AgendaMobileControls from './AgendaMobileControls';
import AgendaSearchResults from './AgendaSearchResults';
import AgendaMobileWeekView from './AgendaMobileWeekView';
import AgendaMobileMonthReadOnly from './AgendaMobileMonthReadOnly';
import AgendaMobileEventEditor from './AgendaMobileEventEditor';
import AgendaMobileTaskEditor from './AgendaMobileTaskEditor';
import AgendaMobileOperationEditor from './AgendaMobileOperationEditor';
import AgendaMobileOperationPicker from './AgendaMobileOperationPicker';
import './AgendaMobile.css';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export function todayStr() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

export function parseLocalDate(dateStr) {
  const [y, m, d] = String(dateStr || todayStr()).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function addDaysStr(dateStr, delta) {
  const d = parseLocalDate(dateStr);
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function weekStartStr(dateStr) {
  const d = parseLocalDate(dateStr);
  const mondayOffset = (d.getDay() + 6) % 7;
  return addDaysStr(dateStr, -mondayOffset);
}

export function weekDaysFor(dateStr) {
  const start = weekStartStr(dateStr);
  return Array.from({ length: 7 }, (_, index) => addDaysStr(start, index));
}

export function monthLabel(ym) {
  const [year, month] = String(ym || '').split('-').map(Number);
  return `${MONTHS[(month || 1) - 1]} ${year || ''}`.trim();
}

export function dayLongLabel(dateStr) {
  const d = parseLocalDate(dateStr);
  const weekday = new Intl.DateTimeFormat('es-UY', { weekday: 'long' }).format(d);
  const month = new Intl.DateTimeFormat('es-UY', { month: 'long' }).format(d);
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${d.getDate()} de ${month}`;
}

export function compactTime(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.slice(0, 5);
}

function monthFromDate(dateStr) {
  return String(dateStr || todayStr()).slice(0, 7);
}

function weekdayLabel(dateStr) {
  if (!dateStr) return '';
  const d = parseLocalDate(dateStr);
  const label = new Intl.DateTimeFormat('es-UY', { weekday: 'long' }).format(d);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function statusDateLabel(dateStr) {
  if (!dateStr) return '';
  const [, , day] = String(dateStr).split('-');
  return `${weekdayLabel(dateStr)} ${Number(day)}`.toUpperCase();
}

function phaseFromItem(item, fallback = 'evento') {
  const phase = String(item?.phase || item?.type || fallback || 'evento').toLowerCase();
  if (phase === 'operacion' || phase === 'operation') return 'evento';
  if (phase === 'setup') return 'armado';
  if (phase === 'breakdown') return 'desarmado';
  return phase;
}

function assignmentPeople(assignments = [], phase = null, limit = 6, me = null) {
  const filtered = assignments.filter(item => !phase || phaseFromItem(item) === phase);
  // Si el usuario logueado está asignado, su casilla va primero (para que no
  // quede escondida detrás del "+N") y marcada. El sort es estable, así que el
  // resto conserva su orden original.
  const tagged = filtered.map(item => ({
    item,
    isMe: !!me && personMatchesName(me, item.person_name || item.name),
  }));
  if (me) tagged.sort((a, b) => (a.isMe === b.isMe ? 0 : a.isMe ? -1 : 1));
  return tagged.slice(0, limit).map(({ item, isMe }) => ({
    id: item.id || `${item.person_name}-${item.role}`,
    name: item.person_name || item.name || 'Sin nombre',
    role: item.role || '',
    isMe,
  }));
}

function peopleCountFromItem(item) {
  const assignments = item?.staff_assignments || [];
  if (assignments.length) return assignments.length;
  const raw = item?.assigned_people || item?.team_operation || item?.team_setup || item?.team_breakdown || '';
  if (!raw) return 0;
  return String(raw).split(/[,·\n]+/).map(x => x.trim()).filter(Boolean).length;
}

function sourceText(parts) {
  return parts.filter(Boolean).join(' · ');
}

function timeRange(start, end) {
  const from = compactTime(start);
  const to = compactTime(end);
  if (from && to) return `${from} - ${to}`;
  return from || to;
}

function eventToCard(event, me = null) {
  const assignments = event.staff_assignments || [];
  const people = assignmentPeople(assignments, null, 5, me);
  const total = assignments.length;
  return {
    id: `event-${event.id}`,
    kind: 'event',
    item: event,
    title: event.name || 'Evento sin nombre',
    // El cliente sólo se ve al entrar al evento: en la tarjeta compite con el lugar.
    subtitle: sourceText([event.place]),
    time: timeRange(event.start_time || event.setup_start, event.end_time),
    status: event.status || '',
    people,
    totalPeople: total || peopleCountFromItem(event),
    warning: !total && !event.team_operation && !event.team_setup && !event.team_breakdown,
    badge: checklistBadge(event),
  };
}

function taskToCard(task, me = null) {
  const assignments = task.staff_assignments || [];
  const people = assignmentPeople(assignments, null, 4, me);
  return {
    id: `task-${task.id}`,
    kind: 'task',
    item: task,
    title: task.title || 'Tarea sin titulo',
    subtitle: sourceText([compactTime(task.time), task.owner, task.event_name]),
    time: compactTime(task.time),
    status: task.status || '',
    people,
    totalPeople: assignments.length || (task.owner ? 1 : 0),
    warning: task.status === 'pendiente' && !assignments.length && !task.owner,
  };
}

function armadoToCard(item, sectionKind = 'armado', eventLookup, me = null) {
  const assignments = item.staff_assignments || [];
  const related = item.event_id ? eventLookup.get(Number(item.event_id)) : null;
  return {
    id: `${sectionKind}-armado-${item.id}`,
    kind: sectionKind,
    item,
    relatedEvent: related,
    title: operationName(item) || (sectionKind === 'desarmado' ? 'Desarmado' : 'Armado'),
    subtitle: sourceText([item.event_place || item.place, item.notes]),
    time: timeRange(item.start_time, item.end_time),
    people: assignmentPeople(assignments, phaseFromItem(item, sectionKind), 4, me),
    totalPeople: assignments.length || peopleCountFromItem(item),
    warning: !assignments.length && !item.assigned_people,
  };
}

function operationToCard(item, eventLookup, me = null) {
  const sectionKind = phaseFromItem(item, 'armado') === 'desarmado' ? 'desarmado' : 'armado';
  const assignments = item.staff_assignments || [];
  const related = item.event_id ? eventLookup.get(Number(item.event_id)) : null;
  return {
    id: `${sectionKind}-operation-${item.id}`,
    kind: sectionKind,
    item,
    relatedEvent: related,
    title: operationName(item) || (sectionKind === 'desarmado' ? 'Desarmado' : 'Armado'),
    subtitle: sourceText([item.event_place || item.place, item.note]),
    time: timeRange(item.start_time, item.end_time),
    people: assignmentPeople(assignments, sectionKind, 4, me),
    totalPeople: assignments.length || peopleCountFromItem(item),
    warning: !assignments.length && !item.assigned_people,
  };
}

export function buildDaySummary({ date, eventsByDate, tasksByDate, armadosByDate, operationsByDate, eventLookup, me = null }) {
  const events = (eventsByDate[date] || []).map(event => eventToCard(event, me));
  const tasks = (tasksByDate[date] || []).map(task => taskToCard(task, me));
  const rawArmados = armadosByDate[date] || [];
  const rawOperations = operationsByDate[date] || [];
  const armados = [
    ...rawArmados.filter(item => phaseFromItem(item, 'armado') !== 'desarmado').map(item => armadoToCard(item, 'armado', eventLookup, me)),
    ...rawOperations.filter(item => phaseFromItem(item, 'armado') !== 'desarmado').map(item => operationToCard(item, eventLookup, me)),
  ];
  const desarmados = [
    ...rawArmados.filter(item => phaseFromItem(item, 'armado') === 'desarmado').map(item => armadoToCard(item, 'desarmado', eventLookup, me)),
    ...rawOperations.filter(item => phaseFromItem(item, 'armado') === 'desarmado').map(item => operationToCard(item, eventLookup, me)),
  ];
  return { date, events, armados, desarmados, tasks };
}

export default function AgendaResponsiveView({
  month,
  setMonth,
  loading,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  globalSearch,
  searchDimensions,
  activeDimensions,
  onToggleDimension,
  onOpenSearchResult,
  allDays,
  eventsByDate,
  tasksByDate,
  armadosByDate,
  operationsByDate,
  canAdmin,
  canOperate,
  canViewEventEquipment,
  canEditEventEquipment,
  onRefresh,
  onAddEvent,
  onUpdateEvent,
  onAddOperation,
  onAddEventSection,
  personnel,
  lugares,
  clients,
  salesRefs,
  assignmentModel,
  assignmentModelError,
  retryAssignmentModel,
}) {
  const { user } = useAuth();
  // Identidad del usuario logueado (de su ficha RRHH) para resaltar sus casillas
  // y ponerlas primero en cada tarjeta. Si no tiene ficha vinculada, queda null
  // y no se resalta nada.
  const me = user?.rrhh || null;

  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('agenda.mobileMode') || 'week'; }
    catch { return 'week'; }
  });
  // El día vive en la URL (?d=AAAA-MM-DD) y se reemplaza en vez de apilarse: así
  // la entrada de historial que queda debajo al entrar a otra pantalla ya sabe
  // dónde estabas, y volver con el gesto de atrás te deja en el mismo día.
  const [searchParams, setSearchParams] = useSearchParams();
  const diaEnUrl = searchParams.get('d');
  const selectedDate = /^\d{4}-\d{2}-\d{2}$/.test(diaEnUrl || '') ? diaEnUrl : todayStr();
  const setSelectedDate = useCallback(valor => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('d', typeof valor === 'function' ? valor(selectedDate) : valor);
      return next;
    }, { replace: true });
  }, [selectedDate, setSearchParams]);
  const [volverAlDia, setVolverAlDia] = useState(0);
  const [editorEvent, setEditorEvent] = useState(null);
  const [editorTask, setEditorTask] = useState(null);
  const [editorOperation, setEditorOperation] = useState(null);
  const [operationPickerType, setOperationPickerType] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('agenda.mobileMode', mode); } catch { /* noop */ }
  }, [mode]);

  useEffect(() => {
    if (monthFromDate(selectedDate) !== month) setMonth(monthFromDate(selectedDate));
  }, [month, selectedDate, setMonth]);

  // Elegir un día es una confirmación táctil barata: se toca un número chico y
  // el contenido de abajo cambia entero. Va acá y no dentro de setSelectedDate
  // porque esa también se llama sola al guardar un evento.
  const elegirDia = useCallback(fecha => {
    haptic.tick();
    setSelectedDate(fecha);
  }, [setSelectedDate]);

  const eventLookup = useMemo(() => {
    const map = new Map();
    Object.values(eventsByDate).flat().forEach(event => map.set(Number(event.id), event));
    return map;
  }, [eventsByDate]);

  const daySummary = useMemo(() => buildDaySummary({
    date: selectedDate,
    eventsByDate,
    tasksByDate,
    armadosByDate,
    operationsByDate,
    eventLookup,
    me,
  }), [selectedDate, eventsByDate, tasksByDate, armadosByDate, operationsByDate, eventLookup, me]);

  const countsByDate = useMemo(() => {
    const counts = {};
    const days = new Set([...allDays, ...weekDaysFor(selectedDate)]);
    for (const date of days) {
      const summary = buildDaySummary({ date, eventsByDate, tasksByDate, armadosByDate, operationsByDate, eventLookup });
      counts[date] = {
        events: summary.events.length,
        armados: summary.armados.length,
        desarmados: summary.desarmados.length,
        tasks: summary.tasks.length,
        total: summary.events.length + summary.armados.length + summary.desarmados.length + summary.tasks.length,
        warnings: [...summary.events, ...summary.armados, ...summary.desarmados, ...summary.tasks].filter(item => item.warning).length,
      };
    }
    return counts;
  }, [allDays, selectedDate, eventsByDate, tasksByDate, armadosByDate, operationsByDate, eventLookup]);

  function shiftVisibleRange(delta) {
    if (mode === 'month') {
      const [year, monthNumber] = month.split('-').map(Number);
      const next = new Date(year, monthNumber - 1 + delta, 1);
      const nextDate = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`;
      setMonth(nextDate.slice(0, 7));
      setSelectedDate(nextDate);
      return;
    }
    setSelectedDate(date => addDaysStr(date, delta * 7));
  }

  function goToday() {
    setSelectedDate(todayStr());
    setMonth(monthFromDate(todayStr()));
    // La tira de días se puede haber arrastrado lejos. Si hoy YA era el día
    // elegido, `selectedDate` no cambia y la tira no se entera de que hay que
    // volver, así que el pedido va por su propio contador.
    setVolverAlDia(n => n + 1);
  }

  function openCard(card) {
    if (card.kind === 'event') {
      setEditorEvent(card.item);
      setIsCreating(false);
      return;
    }
    if (card.kind === 'task') {
      setEditorTask(card.item);
      setIsCreating(false);
      return;
    }
    if (card.kind === 'armado' || card.kind === 'desarmado') {
      setEditorOperation(card.item);
      setIsCreating(false);
    }
  }

  function createTask() {
    setEditorTask({ task_date: selectedDate, status: 'pendiente' });
    setIsCreating(true);
  }

  async function saveEvent(payload) {
    if (isCreating) {
      const { data } = await api.post('/events', { ...payload, days: 1 });
      onAddEvent?.(data);
      await onRefresh?.();
      setSelectedDate(data?.event_date?.slice(0, 10) || payload.event_date || selectedDate);
      setEditorEvent(null);
      setIsCreating(false);
      return;
    }
    const { data } = await api.put(`/events/${editorEvent.id}`, payload);
    const updated = data?.event || data || payload;
    onUpdateEvent?.(editorEvent.id, updated);
    await onRefresh?.();
    setSelectedDate(updated?.event_date?.slice(0, 10) || payload.event_date || selectedDate);
    setEditorEvent(null);
  }

  async function saveTask(payload) {
    if (isCreating) {
      const { data } = await api.post('/tasks', payload);
      await onRefresh?.();
      setSelectedDate(data?.task_date?.slice(0, 10) || payload.task_date || selectedDate);
      setEditorTask(null);
      setIsCreating(false);
      return;
    }
    const { data } = await api.put(`/tasks/${editorTask.id}`, payload);
    await onRefresh?.();
    setSelectedDate(data?.task_date?.slice(0, 10) || payload.task_date || selectedDate);
    setEditorTask(null);
  }

  async function saveOperation(item, payload) {
    const isOperation = item?.entity_type === 'operation' || item?.operation_date;
    if (isOperation) {
      await api.patch(`/event-operations/${item.id}`, {
        operation_date: payload.operation_date,
        title: payload.title,
        place: payload.place,
        note: payload.note,
        start_time: payload.start_time,
        end_time: payload.end_time,
        status: payload.status,
      });
    } else {
      await api.put(`/armados/${item.id}`, {
        armado_date: payload.armado_date,
        title: payload.title,
        place: payload.place,
        notes: payload.note,
        start_time: payload.start_time,
        end_time: payload.end_time,
        status: payload.status,
      });
    }
    await onRefresh?.();
    setSelectedDate((payload.operation_date || payload.armado_date || selectedDate).slice(0, 10));
    setEditorOperation(null);
  }

  async function createOperationFromCandidate(event, type) {
    const baseEventName = operationBaseEventName(event.name);
    const title = type === 'desarmado'
      ? `Desarmado ${baseEventName}`
      : `Armado ${baseEventName}`;
    const statusText = type === 'desarmado'
      ? `DESARMADO EL ${statusDateLabel(selectedDate)}`
      : `YA ESTA ARMADO\n(${statusDateLabel(selectedDate)})`;
    try {
      const { data: operation } = await api.post('/event-operations', {
        event_id: event.id,
        type,
        operation_date: selectedDate,
        place: event.place || null,
        title,
      });
      onAddOperation?.(operation);
      const { data: section } = await api.post('/staff-assignments/sections', {
        event_id: event.id,
        phase: type,
        name: `mobile-${type}-${Date.now()}`,
        note: statusText,
        kind: 'status',
        sort_order: -1,
      });
      onAddEventSection?.(event.id, section);
      await onRefresh?.();
      setOperationPickerType(null);
      setEditorOperation(operation);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo crear la operación.');
    }
  }

  useBackToClose(!!editorEvent, () => { setEditorEvent(null); setIsCreating(false); });
  useBackToClose(!!editorTask, () => { setEditorTask(null); setIsCreating(false); });
  useBackToClose(!!editorOperation, () => setEditorOperation(null));
  useBackToClose(!!operationPickerType, () => setOperationPickerType(null));

  if (editorEvent) {
    return (
      <AgendaMobileEventEditor
        event={editorEvent}
        isCreating={isCreating}
        canEdit={canAdmin}
        canEditAssignments={canOperate || canAdmin}
        canViewEquipment={canViewEventEquipment}
        canEditEquipment={canEditEventEquipment}
        personnel={personnel}
        lugares={lugares}
        clients={clients}
        salesRefs={salesRefs}
        assignmentModel={assignmentModel}
        assignmentModelError={assignmentModelError}
        retryAssignmentModel={retryAssignmentModel}
        onCancel={() => { setEditorEvent(null); setIsCreating(false); }}
        onSave={saveEvent}
      />
    );
  }

  if (editorTask) {
    return (
      <AgendaMobileTaskEditor
        task={editorTask}
        isCreating={isCreating}
        defaultDate={selectedDate}
        canEdit={canOperate || canAdmin}
        canEditAssignments={canOperate || canAdmin}
        personnel={personnel}
        assignmentModel={assignmentModel}
        assignmentModelError={assignmentModelError}
        retryAssignmentModel={retryAssignmentModel}
        onCancel={() => { setEditorTask(null); setIsCreating(false); }}
        onSave={saveTask}
      />
    );
  }

  if (editorOperation) {
    return (
      <AgendaMobileOperationEditor
        item={editorOperation}
        canEdit={canAdmin}
        canEditAssignments={canOperate || canAdmin}
        personnel={personnel}
        assignmentModel={assignmentModel}
        assignmentModelError={assignmentModelError}
        retryAssignmentModel={retryAssignmentModel}
        onCancel={() => setEditorOperation(null)}
        onSave={saveOperation}
      />
    );
  }

  return (
    <main className="agm-shell">
      <AgendaMobileControls
        mode={mode}
        setMode={setMode}
        selectedDate={selectedDate}
        month={month}
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        searchDimensions={searchDimensions}
        activeDimensions={activeDimensions}
        onToggleDimension={onToggleDimension}
        onPrev={() => shiftVisibleRange(-1)}
        onNext={() => shiftVisibleRange(1)}
        onToday={goToday}
      />

      {globalSearch?.active ? (
        <AgendaSearchResults
          query={search}
          results={globalSearch.results}
          total={globalSearch.total}
          loading={globalSearch.loading}
          loadingMore={globalSearch.loadingMore}
          error={globalSearch.error}
          hasMore={globalSearch.hasMore}
          onLoadMore={globalSearch.loadMore}
          onOpen={onOpenSearchResult}
        />
      ) : loading ? (
        <div className="agm-loading"><div className="spinner" /></div>
      ) : mode === 'month' ? (
        <AgendaMobileMonthReadOnly
          month={month}
          allDays={allDays}
          selectedDate={selectedDate}
          volverAlDia={volverAlDia}
          countsByDate={countsByDate}
          daySummary={daySummary}
          onSelectDate={elegirDia}
          onOpenCard={openCard}
        />
      ) : (
        <AgendaMobileWeekView
          selectedDate={selectedDate}
          volverAlDia={volverAlDia}
          countsByDate={countsByDate}
          daySummary={daySummary}
          onSelectDate={elegirDia}
          onOpenCard={openCard}
          onAddTask={canOperate || canAdmin ? createTask : null}
          onAddOperation={canAdmin ? setOperationPickerType : null}
        />
      )}
      {operationPickerType && (
        <AgendaMobileOperationPicker
          type={operationPickerType}
          operationDate={selectedDate}
          onCancel={() => setOperationPickerType(null)}
          onConfirm={event => createOperationFromCandidate(event, operationPickerType)}
        />
      )}
    </main>
  );
}
